import React, { useState } from 'react';
import { T, font, mono } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, Inp, SBadge, IcPlus, IcCheck, IcEdit, IcLock } from './UI';

export default function AdminSuppliers({ lang, suppliers, allSuppliers, reload, Lk }) {
  const [newSup, setNewSup] = useState({ name: '', password: '' });
  // Passwords no longer come with the supplier list: the one being shown is fetched.
  const [shownPw, setShownPw] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const hidePw = (id) => setShownPw(prev => { const next = { ...prev }; delete next[id]; return next; });

  const togglePw = async (id) => {
    if (shownPw[id] !== undefined) return hidePw(id);
    setErr('');
    setShownPw(prev => ({ ...prev, [id]: '' }));
    try {
      const { password } = await api.getSupplierPassword(id);
      setShownPw(prev => ({ ...prev, [id]: password }));
    } catch (e) {
      hidePw(id);
      setErr(e.message);
    }
  };

  const startEdit = async (sup) => {
    setErr('');
    setEditId(sup.id);
    setEditForm({ id: sup.id, name: sup.name, password: '', auto_accept: !!sup.auto_accept, trusted_variants: !!sup.trusted_variants });
    try {
      const { password } = await api.getSupplierPassword(sup.id);
      setEditForm(prev => (prev.id === sup.id ? { ...prev, password } : prev));
    } catch (e) {
      setErr(e.message);
    }
  };

  const active = allSuppliers.filter(s => s.active);
  const inactive = allSuppliers.filter(s => !s.active);

  const addSupplier = async () => {
    if (!newSup.name.trim() || !newSup.password.trim()) return;
    setLoading(true);
    setErr('');
    try {
      await api.createSupplier(newSup.name, newSup.password);
      setNewSup({ name: '', password: '' });
      await reload();
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  const saveEdit = async () => {
    setLoading(true);
    setErr('');
    try {
      await api.updateSupplier(editId, { name: editForm.name, password: editForm.password, auto_accept: editForm.auto_accept ? 1 : 0, trusted_variants: editForm.trusted_variants ? 1 : 0 });
      setEditId(null);
      await reload();
    } catch (e) {
      setErr(e.message);
    }
    setLoading(false);
  };

  const doRemove = async (id) => {
    await api.updateSupplier(id, { active: false });
    await reload();
  };

  const doRestore = async (id) => {
    await api.updateSupplier(id, { active: true });
    await reload();
  };

  return (
    <div>
      <h2 style={{ margin: '0 0 22px', fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('suppliers')} ({active.length})</h2>

      {err && <div style={{ fontSize: 12, color: T.danger, marginBottom: 14 }}>{err}</div>}

      <Sect title={Lk('addSupplier')}>
        <Card>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12, alignItems: 'end' }}>
            <Inp label={Lk('supplierName')} placeholder="e.g. Pacific Materials" value={newSup.name} onChange={e => setNewSup({ ...newSup, name: e.target.value })} />
            <Inp label={Lk('password')} type="password" placeholder="Set password" value={newSup.password} onChange={e => setNewSup({ ...newSup, password: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') addSupplier(); }} />
            <Btn onClick={addSupplier} disabled={loading}>{IcPlus} {Lk('add')}</Btn>
          </div>
          <div style={{ fontSize: 11, color: T.dim, marginTop: 8 }}>
            {lang === 'zh' ? '注意：登录不区分大小写' : 'Note: Login is case-insensitive (capitalization doesn\'t matter)'}
          </div>
        </Card>
      </Sect>

      <Sect title={`${Lk('active')} (${active.length})`}>
        {active.map(s => (
          <Card key={s.id} style={{ marginBottom: 6 }}>
            {editId === s.id ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 10, alignItems: 'center' }}>
                  <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    style={{ fontFamily: font, fontSize: 13, padding: '8px 10px', background: T.bg, border: `1px solid ${T.accent}50`, borderRadius: 6, color: T.text, outline: 'none' }} />
                  <input value={editForm.password} onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                    placeholder={lang === 'zh' ? '留空则密码不变' : 'Leave blank to keep the current password'}
                    style={{ fontFamily: font, fontSize: 13, padding: '8px 10px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Btn variant="small" onClick={saveEdit}>{IcCheck} {Lk('save')}</Btn>
                    <Btn variant="ghost" onClick={() => setEditId(null)}>{Lk('cancel')}</Btn>
                  </div>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: T.muted, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={!!editForm.auto_accept} onChange={e => setEditForm({ ...editForm, auto_accept: e.target.checked })}
                    style={{ accentColor: T.success, width: 14, height: 14, cursor: 'pointer' }} />
                  {lang === 'zh' ? '自动接受订单（此供应商不使用系统）' : 'Auto-accept orders — skip supplier confirmation step'}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, color: T.muted, cursor: 'pointer', userSelect: 'none' }}>
                  <input type="checkbox" checked={!!editForm.trusted_variants} onChange={e => setEditForm({ ...editForm, trusted_variants: e.target.checked })}
                    style={{ accentColor: T.success, width: 14, height: 14, cursor: 'pointer' }} />
                  {lang === 'zh' ? '信任其建议的版本 — 直接开放给所有供应商，无需审核' : 'Trust suggested versions — open them to all suppliers without review'}
                </label>
              </div>
            ) : (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, color: T.text, fontSize: 14 }}>{s.name}</span>
                    {s.auto_accept ? <SBadge label={lang === 'zh' ? '自动接受' : 'Auto-accept'} color={T.success} bg={T.successBg} /> : null}
                    {s.trusted_variants ? <SBadge label={lang === 'zh' ? '信任版本' : 'Trusted versions'} color={T.accent} bg={T.surfaceAlt} /> : null}
                  </div>
                  <div style={{ fontSize: 11, color: T.dim, marginTop: 2 }}>{Lk('since')} {s.created_at}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.bg, padding: '5px 10px', borderRadius: 6, border: `1px solid ${T.border}` }}>
                    {IcLock}
                    <span style={{ fontFamily: mono, fontSize: 12, color: T.muted }}>{shownPw[s.id] !== undefined ? (shownPw[s.id] || '…') : '••••••••'}</span>
                    <span onClick={() => togglePw(s.id)} style={{ cursor: 'pointer', color: T.accent, fontSize: 10, fontWeight: 700, marginLeft: 4 }}>
                      {shownPw[s.id] !== undefined ? Lk('hide') : Lk('show')}
                    </span>
                  </div>
                  <Btn variant="ghostSm" onClick={() => startEdit(s)}>{IcEdit}</Btn>
                  <Btn variant="dangerSm" onClick={() => doRemove(s.id)}>{Lk('remove')}</Btn>
                </div>
              </div>
            )}
          </Card>
        ))}
      </Sect>

      {inactive.length > 0 && (
        <Sect title={Lk('inactive')}>
          {inactive.map(s => (
            <Card key={s.id} style={{ marginBottom: 6, opacity: 0.5 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 600, color: T.muted }}>{s.name}</span>
                <Btn variant="ghost" onClick={() => doRestore(s.id)}>{Lk('restore')}</Btn>
              </div>
            </Card>
          ))}
        </Sect>
      )}
    </div>
  );
}
